import { c as _c } from "react/compiler-runtime";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import { MARKER_3D_LIFT_MIN_TILT_DEG } from "@components/world-map/world-map-constants";
import { useEffect, useState } from "react";

/**
 * Tracks whether the 3D markers should be grounded or lifted, based on the
 * camera tilt.
 *
 * Near top-down (tilt below {@link MARKER_3D_LIFT_MIN_TILT_DEG}) the markers rest
 * on their exact coordinate with no callout line; once the camera tilts past the
 * threshold they are raised above the surface with a leader line so buildings
 * don't occlude them. The state only flips when the threshold is crossed, so
 * consumers (the clustering pins and the highlight overlay) swap their symbols
 * at most once per crossing, not on every tilt change. Always false for the 2D
 * `MapView`.
 *
 * @param viewRef - Ref to the ArcGIS view (only the 3D `SceneView` is handled).
 * @param isViewReady - Whether the view has finished initializing.
 * @returns Whether the markers are currently lifted.
 */
export function useMarker3DLift(viewRef, isViewReady) {
  const $ = _c(4);
  const [isLifted, setIsLifted] = useState(false);
  let t0;
  let t1;
  if ($[0] !== isViewReady || $[1] !== viewRef) {
    t0 = () => {
      const view = viewRef.current;
      if (!isViewReady || !view || view.type !== "3d") {
        return;
      }
      const handle = reactiveUtils.watch(() => (view.camera?.tilt ?? 0) >= MARKER_3D_LIFT_MIN_TILT_DEG, isLiftedNow => setIsLifted(isLiftedNow), {
        initial: true
      });
      return () => handle.remove();
    };
    t1 = [viewRef, isViewReady];
    $[0] = isViewReady;
    $[1] = viewRef;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return isLifted;
}